import { c as _c } from "react/compiler-runtime";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeRangeTabs } from "@/components/ui/time-range-tabs";
import { cn } from "@/lib/utils";
interface TestDetailHeaderProps {
  // Tab state
  activeTab: "logs" | "history";
  onTabChange: (tab: "logs" | "history") => void;
  showTabToggle?: boolean;

  // Time range state
  timeRangeHours: number;
  onTimeRangeChange: (hours: number) => void;

  // Branch filter state
  branchFilter: "all" | "main" | "current" | "main_and_current" | "latest_per_branch";
  onBranchFilterChange: (filter: "all" | "main" | "current" | "main_and_current" | "latest_per_branch") => void;

  // Zoom state (optional)
  zoomStart?: number | null;
  zoomEnd?: number | null;
  onZoomChange?: (start: number | null, end: number | null) => void;
}
export const TestDetailHeader = React.memo(function TestDetailHeader(t0) {
  const $ = _c(31);
  const {
    activeTab,
    onTabChange,
    showTabToggle: t1,
    timeRangeHours,
    onTimeRangeChange,
    branchFilter,
    onBranchFilterChange,
    zoomStart,
    zoomEnd,
    onZoomChange
  } = t0;
  const showTabToggle = t1 === undefined ? true : t1;
  const tabContainerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      history: null,
      logs: null
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const tabButtonRefs = useRef(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      left: 0,
      width: 0,
      ready: false
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [tabIndicator, setTabIndicator] = useState(t3);
  let t4;
  if ($[2] !== activeTab) {
    t4 = () => {
      const containerEl = tabContainerRef.current;
      const activeButtonEl = tabButtonRefs.current[activeTab];
      if (!containerEl || !activeButtonEl) {
        return;
      }
      setTabIndicator({
        left: activeButtonEl.offsetLeft,
        width: activeButtonEl.offsetWidth,
        ready: true
      });
    };
    $[2] = activeTab;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const updateTabIndicator = t4;
  let t5;
  let t6;
  if ($[4] !== updateTabIndicator) {
    t5 = () => {
      const containerEl_0 = tabContainerRef.current;
      if (!containerEl_0) {
        return;
      }
      const rafId = requestAnimationFrame(() => {
        updateTabIndicator();
      });
      const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => updateTabIndicator()) : null;
      observer?.observe(containerEl_0);
      return () => {
        cancelAnimationFrame(rafId);
        observer?.disconnect();
      };
    };
    t6 = [updateTabIndicator];
    $[4] = updateTabIndicator;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useLayoutEffect(t5, t6);
  let t7;
  if ($[7] !== activeTab || $[8] !== onTabChange) {
    t7 = t8 => {
      const {
        tab,
        label,
        buttonRef
      } = t8;
      const isActive = activeTab === tab;
      return <button ref={buttonRef} type="button" onClick={() => onTabChange(tab)} className={cn("relative z-10 flex items-center justify-center h-[22px] px-3 text-xs rounded-md border transition-colors", "bg-transparent border-transparent", isActive ? "cursor-default text-neutral-900 dark:text-white font-medium" : "cursor-pointer text-neutral-900 dark:text-neutral-200/80 font-normal")}>{label}</button>;
    };
    $[7] = activeTab;
    $[8] = onTabChange;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const TabButton = t7;
  let t8;
  if ($[10] !== TabButton || $[11] !== showTabToggle || $[12] !== tabIndicator) {
    t8 = showTabToggle && <div ref={tabContainerRef} className="relative flex items-center bg-[#F5F5F5] dark:bg-neutral-800 rounded-lg p-px"><div aria-hidden={true} className={cn("absolute left-0 top-px bottom-px rounded-md border", "bg-white dark:bg-neutral-900 border-black/[0.24] dark:border-white/20", "transition-[transform,width] duration-150 ease-out will-change-[transform,width]", !tabIndicator.ready && "opacity-0")} style={{
        width: `${tabIndicator.width}px`,
        transform: `translateX(${tabIndicator.left}px)`
      }} /><TabButton tab="history" label="History" buttonRef={el => {
        tabButtonRefs.current.history = el;
      }} /><TabButton tab="logs" label="Logs" buttonRef={el_0 => {
        tabButtonRefs.current.logs = el_0;
      }} /></div>;
    $[10] = TabButton;
    $[11] = showTabToggle;
    $[12] = tabIndicator;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== activeTab || $[15] !== branchFilter || $[16] !== onBranchFilterChange) {
    t9 = activeTab === "history" && <Select value={branchFilter} onValueChange={value => onBranchFilterChange(value as "all" | "main" | "current" | "main_and_current" | "latest_per_branch")}><SelectTrigger className="h-[26px] bg-white dark:bg-neutral-900 border border-black/[0.13] dark:border-white/20 rounded-lg px-2 text-xs font-normal text-[#161718] dark:text-white gap-1"><SelectValue>{branchFilter === "all" ? "All runs" : branchFilter === "main" ? "Main only" : branchFilter === "current" ? "Current branch" : branchFilter === "main_and_current" ? "Main and current branch" : "Latest per branch"}</SelectValue></SelectTrigger><SelectContent><SelectItem value="all">All runs</SelectItem><SelectItem value="main">Main only</SelectItem><SelectItem value="current">Current branch</SelectItem><SelectItem value="main_and_current">Main and current branch</SelectItem><SelectItem value="latest_per_branch">Latest per branch</SelectItem></SelectContent></Select>;
    $[14] = activeTab;
    $[15] = branchFilter;
    $[16] = onBranchFilterChange;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <div className="flex items-center gap-4">{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== activeTab || $[22] !== onTimeRangeChange || $[23] !== onZoomChange || $[24] !== timeRangeHours || $[25] !== zoomEnd || $[26] !== zoomStart) {
    t11 = activeTab === "history" && <div className="flex items-center gap-2"><TimeRangeTabs value={timeRangeHours} onValueChange={onTimeRangeChange} />{zoomStart && zoomEnd && <><span className="text-xs text-[#737373] dark:text-[#A3A3A3]">{format(new Date(zoomStart), "MMM d, h:mma")} -{" "}{format(new Date(zoomEnd), "MMM d, h:mma")}</span>{onZoomChange && <button onClick={() => onZoomChange(null, null)} className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300">Reset</button>}</>}</div>;
    $[21] = activeTab;
    $[22] = onTimeRangeChange;
    $[23] = onZoomChange;
    $[24] = timeRangeHours;
    $[25] = zoomEnd;
    $[26] = zoomStart;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <div className="flex items-center justify-between gap-4 px-4 h-[42px] border-b border-[var(--color-border)] dark:border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">{t10}{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
});